import React from "react";
import { useSelector } from "react-redux";

import { IgPaper, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { ExperienceFe } from "@intelligems/ig-types";
import {
  getEndedAtTs,
  getFirstStartedAtTs,
  getPausedAtTs,
  getStartedAtTs,
} from "@intelligems/ig-analytics";
import { Box, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { ExperienceOverviewMetrics } from "@src/features/analytics/baseDashboard/ExperienceOverviewMetrics";
import { selectedExperience } from "@src/store/slices/experiences";
import { shortDateTimeConverter } from "@src/utils/dateConverters";

interface ExperimentFloatingDetailsBarProps {
  latestRunTs: number | undefined;
  experienceTotals: {
    visitors: number;
    orders: number;
    revenue: number | null;
  };
  shouldRenderGraphs: boolean;
  hidePaperBorder?: boolean;
  isDataLoading: boolean;
  // Optional overrides to avoid redundant API calls when data is already available in context
  experience?: ExperienceFe;
  timezone?: string;
}
export const ExperienceFloatingDetailsBar = ({
  latestRunTs,
  experienceTotals,
  shouldRenderGraphs,
  hidePaperBorder,
  isDataLoading,
  experience: propExperience,
  timezone: propTimezone,
}: ExperimentFloatingDetailsBarProps) => {
  const selectorExperience = useSelector(selectedExperience);
  const organization = useGetOrg();
  const theme = useTheme();

  // Use prop if provided, otherwise fall back to Redux/API
  const experience = propExperience ?? selectorExperience;
  const timezone = propTimezone ?? organization?.data?.org.timezone ?? "UTC";
  return (
    <Grid container sx={{ width: "100%" }} size={12}>
      <IgPaper
        elevation={0}
        sx={{
          width: "100%",
          padding: "16px 16px",
          // border: hidePaperBorder ? "none" : "1px solid #ECECEC",
          // boxShadow: "none",
        }}
      >
        <Grid
          container
          rowSpacing={1}
          sx={{ padding: "0px !important", width: "100%" }}
          alignItems={"center"}
        >
          <Grid
            container
            direction={"row"}
            wrap={"nowrap"}
            justifyContent={"space-between"}
            rowSpacing={1}
            alignItems={"center"}
            size={12}
          >
            <Grid
              container
              direction={"row"}
              columnSpacing={3.75}
              justifyContent={"flex-start"}
              rowSpacing={1}
              // flex={1}
              alignItems={"center"}
              wrap={"nowrap"}
            >
              <Grid>
                <Stack direction={"column"} spacing={1}>
                  <IgTypography
                    variant={"body2"}
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Start Date
                  </IgTypography>

                  <IgTypography variant={"body2"}>
                    {experience.status === "pending"
                      ? "--"
                      : shortDateTimeConverter(
                          getFirstStartedAtTs(experience.experienceActions),
                          undefined,
                          undefined,
                          undefined,
                          true
                        )}
                  </IgTypography>
                </Stack>
              </Grid>
              <Grid>
                <Stack spacing={1} direction={"column"}>
                  <IgTypography
                    variant={"body2"}
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {experience.category === "personalization"
                      ? "Stop Date"
                      : "End Date"}
                  </IgTypography>

                  <IgTypography variant={"body2"}>
                    {experience.status !==
                    (experience.category === "personalization"
                      ? "paused"
                      : "ended")
                      ? "--"
                      : shortDateTimeConverter(
                          experience.category === "personalization"
                            ? getPausedAtTs(experience.experienceActions)
                                ?.actionTs
                            : getEndedAtTs(experience.experienceActions)
                                ?.actionTs,
                          undefined,
                          undefined,
                          undefined,
                          true
                        )}
                  </IgTypography>
                </Stack>
              </Grid>
              <Grid>
                <Stack spacing={1} direction={"column"}>
                  <IgTypography
                    variant={"body2"}
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Timezone
                  </IgTypography>

                  <IgTooltip
                    title={
                      "This is the default timezone of your Shopify store and the timezone Intelligems analytics is set to."
                    }
                  >
                    <Grid container alignItems={"center"}>
                      <IgTypography
                        variant={"body2"}
                        sx={{
                          ml: 0.5,
                        }}
                      >
                        {timezone.replaceAll("_", " ")}
                      </IgTypography>
                    </Grid>
                  </IgTooltip>
                </Stack>
              </Grid>
            </Grid>
            <Grid size={3}>
              <ExperienceOverviewMetrics
                experienceTotals={experienceTotals}
                isDataLoading={isDataLoading}
              />
            </Grid>
          </Grid>
        </Grid>
      </IgPaper>
    </Grid>
  );
};
